import { useEffect, useMemo, useRef, useState } from 'react';
import { Folder, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'hooks/use-debounce';
import { cn } from 'lib/utils';
import { IFileDataWithSharing } from '../../utils/file-manager';
import {
  LIBRARY_FOLDERS_PAGE_SIZE,
  LIBRARY_ROOT_DIRECTORY_ID,
  type LibraryFolder,
} from '../../services/library-folders-service';
import { useListLibraryFoldersInfinite } from '../../hooks/use-library-folders';

type MoveToFolderModalProps = {
  isOpen: boolean;
  file: IFileDataWithSharing | null;
  onClose: () => void;
  onConfirm: (parentDirectoryId: string) => void;
};

const folderLabel = (folder: LibraryFolder) => folder.path || folder.FileName;

export const MoveToFolderModal = ({ isOpen, file, onClose, onConfirm }: MoveToFolderModalProps) => {
  const { t } = useTranslation();
  const currentParentId = file?.parentDirectoryId || LIBRARY_ROOT_DIRECTORY_ID;
  const [selectedId, setSelectedId] = useState(currentParentId);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const trimmedSearch = debouncedSearch.trim();
  const listContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useListLibraryFoldersInfinite(
    {
      pageSize: LIBRARY_FOLDERS_PAGE_SIZE,
      search: trimmedSearch || undefined,
    },
    { enabled: isOpen }
  );

  const folders = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data?.pages]
  );

  useEffect(() => {
    if (isOpen && file) {
      setSelectedId(file.parentDirectoryId || LIBRARY_ROOT_DIRECTORY_ID);
    }
  }, [file, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    const scrollContainer = listContainerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!isOpen || !scrollContainer || !loadMoreEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage, folders.length, isLoading]);

  if (!file) return null;

  const handleClose = () => {
    setSelectedId(file.parentDirectoryId || LIBRARY_ROOT_DIRECTORY_ID);
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('MOVE_TO_FOLDER', { defaultValue: 'Move to folder' })}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('MOVE_FILE_TO_FOLDER_HINT', {
            defaultValue: 'Choose a folder for “{{name}}”.',
            name: file.name,
          })}
        </p>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('SEARCH_FOLDERS', { defaultValue: 'Search folders' })}
            className="pl-9"
          />
        </div>

        <div ref={listContainerRef} className="max-h-72 space-y-1 overflow-y-auto">
          <button
            type="button"
            onClick={() => setSelectedId(LIBRARY_ROOT_DIRECTORY_ID)}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm',
              selectedId === LIBRARY_ROOT_DIRECTORY_ID
                ? 'bg-[#f6f6f6] text-[#3d3d3d] dark:bg-white/10 dark:text-foreground'
                : 'text-[#3d3d3d] hover:bg-[#f6f6f6] dark:text-foreground dark:hover:bg-white/10'
            )}
          >
            <Folder className="size-4 shrink-0" />
            {t('MEDIA_LIBRARY', { defaultValue: 'Media Library' })}
          </button>
          {folders.map((folder) => (
            <button
              key={folder.FileId}
              type="button"
              onClick={() => setSelectedId(folder.FileId)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm',
                selectedId === folder.FileId
                  ? 'bg-[#f6f6f6] text-[#3d3d3d] dark:bg-white/10 dark:text-foreground'
                  : 'text-[#3d3d3d] hover:bg-[#f6f6f6] dark:text-foreground dark:hover:bg-white/10'
              )}
            >
              <Folder className="size-4 shrink-0" />
              <span className="min-w-0 truncate">{folderLabel(folder)}</span>
            </button>
          ))}
          <div ref={loadMoreRef} className="h-1" aria-hidden />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={handleClose}>
            {t('CANCEL')}
          </Button>
          <Button
            disabled={!selectedId || selectedId === currentParentId}
            onClick={() => {
              onConfirm(selectedId);
              handleClose();
            }}
          >
            {t('MOVE', { defaultValue: 'Move' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
